import React, { useMemo, ReactElement } from 'react';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import ChipLink from '../../../components/chip-link/chip-link.component';
import FieldAddressBlock from '../../../components/field-address-block/field-address-block.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldCheckbox from '../../../components/form-field-checkbox/form-field-checkbox.component';
import FormFieldAddress from '../../../components/form-field-address/form-field-address.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import SinglePageTab from '../../app/common/single-page-tab/single-page-tab.component';
import FormatService from '../../../services/format-service';
import FormService from '../../../services/form-service';
import { DEAL_VIEW } from '../../../constants/entity/permissions';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';

const AssetLocationInformation: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    isMultiplePortfolioGroupView,
    formError,
    portfolioList,
    assetData,
    onDealClick,
    onCancelForm,
    onSubmitForm,
    onAddressFieldKeyDown,
    onAddressSearchCompleted,
  } = props;

  const formPrefix: string = 'assetData';
  const asOfRightZoningLabel: string = assetData && FormatService.formatBooleanValue(assetData.asOfRightZoning, true);

  const menuItemPortfolioList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    isGrouped: isMultiplePortfolioGroupView,
    groupKey: 'group',
    groupItemLabelKey: 'name',
    optionItemLabelKey: 'name',
    optionList: portfolioList,
  }), [portfolioList, isMultiplePortfolioGroupView]);

  return (
    <SinglePageTab
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <Table>
        <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
          <TableRow>
            <TableCell>
              Name
              {
                isEditMode && (
                  <RequiredMark />
                )
              }
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldText isBordered name={`${formPrefix}.name`} />
                ) : assetData.name
              }
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Portfolio
              {
                isEditMode && (
                  <RequiredMark />
                )
              }
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldSelect
                    isBordered
                    withBlankElement
                    isDisabled={portfolioList && !portfolioList.length}
                    name={`${formPrefix}.portfolioId`}
                    optionsTotal={portfolioList && portfolioList.length}
                  >
                    {menuItemPortfolioList}
                  </FormFieldSelect>
                ) : assetData.portfolio.name
              }
            </TableCell>
          </TableRow>
          {
            !isEditMode && assetData.lastDeal && (
              <TableRow>
                <TableCell>
                  Linked Deal
                </TableCell>
                <TableCell>
                  <ChipLink
                    isDisabled={isEditMode}
                    label={assetData.lastDeal.name}
                    imageLink={assetData.lastDeal.mainPhotoThumbnail || assetData.lastDeal.mainPhoto
                      || AssetDefaultImage}
                    permission={DEAL_VIEW}
                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                      onDealClick(event, assetData.lastDeal.id);
                    }}
                  />
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.streetAddress) && (
              <TableRow>
                <TableCell>
                  Address
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FieldAddressBlock>
                        <FormFieldAddress
                          isModal
                          isBordered
                          name={`${formPrefix}.streetAddress`}
                          onAddressSearchCompleted={onAddressSearchCompleted}
                          onKeyDown={onAddressFieldKeyDown}
                        />
                        <FormFieldText isDisabled isBordered name={`${formPrefix}.location`} />
                        <FormFieldText isDisabled isBordered name={`${formPrefix}.postalCode`} />
                        <FormFieldText isHidden name={`${formPrefix}.address`} />
                      </FieldAddressBlock>
                    ) : (
                      <FilteredLink isExternal link={assetData.address && assetData.address.url}>
                        {
                          FormatService.formatStringAddress(
                            assetData.streetAddress,
                            assetData.location,
                            assetData.postalCode,
                          )
                        }
                      </FilteredLink>
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.addressDetails) && (
              <TableRow>
                <TableCell>
                  Address Details
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText
                        isBordered
                        isMultiline
                        name={`${formPrefix}.addressDetails`}
                        maxRows={FormService.TEXTAREA_MAX_ROWS_SMALL}
                        minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                      />
                    ) : assetData.addressDetails
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (!isEditMode && assetData.msaLocation) && (
              <TableRow>
                <TableCell>
                  MSA Location
                </TableCell>
                <TableCell>
                  {assetData.msaLocation}
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.parcelNumber) && (
              <TableRow>
                <TableCell>
                  Location Property Identifier
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.parcelNumber`} />
                    ) : assetData.parcelNumber
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.marketType) && (
              <TableRow>
                <TableCell>
                  Market Type
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.marketType`} />
                    ) : assetData.marketType
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.submarket) && (
              <TableRow>
                <TableCell>
                  Submarket
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.submarket`} />
                    ) : assetData.submarket
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || !!asOfRightZoningLabel) && (
              <TableRow>
                <TableCell>
                  As-of-Right Zoning
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldCheckbox name={`${formPrefix}.asOfRightZoning`} />
                    ) : (asOfRightZoningLabel || null)
                  }
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
    </SinglePageTab>
  );
});

export default AssetLocationInformation;
